import { alpha, Palette } from '@mui/material/styles';

import colorPalette from 'common/theme/colorPalette';

import {
  KraMetricsEnum,
  ObjectiveAttentionTypeEnum,
  ObjectiveCategoryEnum,
  ObjectiveStatusEnum,
  ObjectiveTypeEnum,
} from './objectives.enum';
import { ObjectiveAttentionItem } from './types/objective.type';

export const OBJECTIVE_DATE_FORMAT = 'YYYY-MM-DD';
export const OBJECTIVE_DATE_INPUT_FORMAT = 'dd-MM-yyyy';

export const OBJECTIVE_TITLE_CHARACTER_LIMIT = 128;
export const OBJECTIVE_DESCRIPTION_CHARACTER_LIMIT = 1000;
export const OBJECTIVE_COMMENT_CHARACTER_LIMIT = 2000;

export const OBJECTIVE_COMMENT_DATE_TIME_FORMAT = 'MMM D, YYYY · h:mm A';

export const MIN_OBJECTIVE_DURATION_MONTHS = 1;
export const MAX_OBJECTIVE_DURATION_MONTHS = 3;

/** Which set of objectives the dashboard is showing. */
export enum ObjectivesViewEnum {
  INDIVIDUAL = 'individual',
  TEAM = 'team',
}

export const ACTIVE_OBJECTIVE_STATUSES = [
  ObjectiveStatusEnum.ON_TRACK,
  ObjectiveStatusEnum.DRAFT,
  ObjectiveStatusEnum.TODO,
];

export const BLOCKED_OBJECTIVE_STATUSES = [
  ObjectiveStatusEnum.AT_RISK,
  ObjectiveStatusEnum.DELAYED,
];

/** The "Draft / To Do" segment of the health bar - the statuses with no work logged yet. */
export const NOT_STARTED_OBJECTIVE_STATUSES = [
  ObjectiveStatusEnum.DRAFT,
  ObjectiveStatusEnum.TODO,
];

export const OBJECTIVE_CATEGORY_OPTIONS = [
  { value: ObjectiveCategoryEnum.INDIVIDUAL, label: 'Individual' },
  { value: ObjectiveCategoryEnum.TEAM, label: 'Team' },
  { value: ObjectiveCategoryEnum.ORGANIZATION, label: 'Organization' },
];

export const OBJECTIVE_TYPE_OPTIONS = [
  { value: ObjectiveTypeEnum.PERFORMANCE, label: 'Performance' },
  { value: ObjectiveTypeEnum.GROWTH, label: 'Growth' },
];

// Team objectives require a manager; Organization objectives require an L1 user (a direct report of the CEO).
export const getCreateObjectiveCategoryOptions = (
  isManager: boolean,
  canCreateOrganizationObjective: boolean,
) => {
  const options = [{ value: ObjectiveCategoryEnum.INDIVIDUAL, label: 'Individual' }];

  if (isManager) {
    options.push({ value: ObjectiveCategoryEnum.TEAM, label: 'Team' });
  }

  if (canCreateOrganizationObjective) {
    options.push({ value: ObjectiveCategoryEnum.ORGANIZATION, label: 'Organization' });
  }

  return options;
};

export const KRA_METRICS_OPTIONS = [
  { value: KraMetricsEnum.MINUTES, label: 'Minutes' },
  { value: KraMetricsEnum.HOURS, label: 'Hours' },
  { value: KraMetricsEnum.DAYS, label: 'Days' },
  { value: KraMetricsEnum.PERCENTAGE, label: 'Percentage' },
  { value: KraMetricsEnum.UNIT, label: 'Unit' },
];

export const NO_PARENT_OBJECTIVE_LABEL = '— none —';

export const FIELD_CHANGE_LABELS = new Map<string, string>([
  ['startValue', 'Start Value'],
  ['targetValue', 'Target Value'],
  ['currentValue', 'Current Value'],
  ['metrics', 'Metric'],
  ['targetDate', 'Target Date'],
  ['description', 'Description'],
  ['category', 'Category'],
  ['type', 'Type'],
  ['startDate', 'Start Date'],
  ['endDate', 'End Date'],
  ['parentId', 'Parent Objective'],
]);

export const OBJECTIVE_CATEGORY_LABELS = new Map(
  OBJECTIVE_CATEGORY_OPTIONS.map((option) => [option.value as string, option.label]),
);

export const OBJECTIVE_TYPE_LABELS = new Map(
  OBJECTIVE_TYPE_OPTIONS.map((option) => [option.value as string, option.label]),
);

export const OBJECTIVE_STATUS_LABELS: Record<ObjectiveStatusEnum, string> = {
  [ObjectiveStatusEnum.DRAFT]: 'Draft',
  [ObjectiveStatusEnum.ON_TRACK]: 'On Track',
  [ObjectiveStatusEnum.TODO]: 'To Do',
  [ObjectiveStatusEnum.ARCHIVED]: 'Archived',
  [ObjectiveStatusEnum.COMPLETED]: 'Completed',
  [ObjectiveStatusEnum.DELAYED]: 'Delayed',
  [ObjectiveStatusEnum.AT_RISK]: 'At Risk',
  [ObjectiveStatusEnum.PENDING_REVIEW]: 'Pending Review',
};

export type ObjectiveAttentionAction = 'updateProgress' | 'addComment' | 'createDevGoal';

type ObjectiveAttentionConfig = {
  getMessage: (item: ObjectiveAttentionItem) => string;
  actionLabel: string;
  action: ObjectiveAttentionAction;
  dotColor: string;
};

const quoted = (item: ObjectiveAttentionItem) => `"${item.objectiveTitle}"`;

export const OBJECTIVE_ATTENTION_CONFIG = new Map<
  ObjectiveAttentionTypeEnum,
  ObjectiveAttentionConfig
>([
  [
    ObjectiveAttentionTypeEnum.DELAYED,
    {
      getMessage: (item: ObjectiveAttentionItem) => `${quoted(item)} is DELAYED.`,
      actionLabel: 'Update Progress',
      action: 'updateProgress',
      dotColor: colorPalette.RED400,
    },
  ],
  [
    ObjectiveAttentionTypeEnum.AT_RISK,
    {
      getMessage: (item: ObjectiveAttentionItem) => `${quoted(item)} is AT_RISK.`,
      actionLabel: 'Add Comment',
      action: 'addComment',
      dotColor: colorPalette.ORANGE500,
    },
  ],
  [
    ObjectiveAttentionTypeEnum.ENDING_SOON,
    {
      getMessage: (item: ObjectiveAttentionItem) =>
        item.daysUntilEndDate === 0
          ? `${quoted(item)} ends today.`
          : `${quoted(item)} ends in ${item.daysUntilEndDate} day${
              item.daysUntilEndDate === 1 ? '' : 's'
            }.`,
      actionLabel: 'Update Progress',
      action: 'updateProgress',
      dotColor: colorPalette.ORANGE500,
    },
  ],
  [
    ObjectiveAttentionTypeEnum.STALE,
    {
      getMessage: (item: ObjectiveAttentionItem) =>
        `No updates on ${quoted(item)} in 30+ days.`,
      actionLabel: 'Update Progress',
      action: 'updateProgress',
      dotColor: colorPalette.GRAY400,
    },
  ],
  [
    ObjectiveAttentionTypeEnum.NO_GROWTH_OBJECTIVE,
    {
      getMessage: () => 'You have no Development objective this cycle.',
      actionLabel: 'Create Dev Goal',
      action: 'createDevGoal',
      dotColor: colorPalette.GRAY400,
    },
  ],
]);

export const getObjectiveStatusColor = (
  status: ObjectiveStatusEnum,
  palette: Palette,
): string => {
  switch (status) {
    case ObjectiveStatusEnum.ON_TRACK:
    case ObjectiveStatusEnum.COMPLETED:
      return palette.StatusDropdown.Open.LabelAndIconDefault;
    case ObjectiveStatusEnum.AT_RISK:
      return colorPalette.YELLOW400;
    case ObjectiveStatusEnum.DELAYED:
      return palette.Failure;
    case ObjectiveStatusEnum.PENDING_REVIEW:
      return palette.SelectionChips.Primary.BgDefault;
    default:
      return palette.StatusDropdown.Closed.LabelAndIconDefault;
  }
};

export const getObjectiveStatusOptions = (palette: Palette) =>
  Object.values(ObjectiveStatusEnum).map((status) => {
    const color = getObjectiveStatusColor(status, palette);

    return {
      value: status,
      label: OBJECTIVE_STATUS_LABELS[status],
      dropdownLabel: OBJECTIVE_STATUS_LABELS[status],
      bgColor: alpha(color, 0.16),
      bgHover: alpha(color, 0.24),
      color,
    };
  });
